<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  reason: {
    type: Object,
    required: true,
  },
  level: {
    type: Number,
    default: 0,
  },
  isExpanded: {
    type: Boolean,
    default: false,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  expandedReasons: {
    type: Set,
    default: () => new Set(),
  },
  selectedReasons: {
    type: Set,
    default: () => new Set(),
  },
});

const emit = defineEmits(['toggleExpand', 'toggleSelection']);

const hasChildren = computed(() => {
  return props.reason.childReasons && props.reason.childReasons.length > 0;
});

const paddingStyle = computed(() => {
  return {
    paddingLeft: `${props.level * 1.5 + 0.75}rem`,
  };
});

const reasonColor = computed(() => {
  return props.reason.config?.color || '#94a3b8'; // Default to slate-400
});

const isChildExpanded = reasonId => {
  return props.expandedReasons.has(reasonId);
};

const isChildSelected = reasonId => {
  return props.selectedReasons.has(reasonId);
};

const handleToggle = () => {
  if (hasChildren.value) {
    emit('toggleExpand', props.reason.id);
  }
};

const handleSelect = e => {
  e.stopPropagation();
  emit('toggleSelection', props.reason.id);
};
</script>

<template>
  <div>
    <div
      v-tooltip.bottom="reason.description"
      class="group flex items-center gap-2 px-3 py-2.5 transition-colors rounded-lg"
      :class="{ 'cursor-pointer': hasChildren, 'cursor-default': !hasChildren }"
      :style="paddingStyle"
      @mouseenter="
        $event.currentTarget.style.backgroundColor = reasonColor + '20'
      "
      @mouseleave="$event.currentTarget.style.backgroundColor = 'transparent'"
      @click="handleToggle"
    >
      <Icon
        icon="i-lucide-tag"
        class="flex-shrink-0 w-4 h-4 transition-all"
        :style="{ color: reasonColor }"
      />

      <div class="flex-1 min-w-0">
        <div
          class="text-sm font-medium transition-all"
          :style="{ color: isSelected ? reasonColor : undefined }"
          :class="{ 'text-slate-900 dark:text-slate-100': !isSelected }"
        >
          {{ reason.name }}
        </div>
      </div>

      <input
        v-if="!hasChildren"
        type="checkbox"
        :checked="isSelected"
        class="w-5 h-5 flex-shrink-0 cursor-pointer rounded accent-woot-500"
        @click="handleSelect"
      />
    </div>
    <div
      v-if="hasChildren"
      class="overflow-hidden transition-all duration-500 ease-in-out"
      :style="{
        maxHeight: isExpanded ? '1000px' : '0px',
        opacity: isExpanded ? '1' : '0',
      }"
    >
      <ContactReasonTreeNode
        v-for="childReason in reason.childReasons"
        :key="childReason.id"
        :reason="childReason"
        :level="level + 1"
        :is-expanded="isChildExpanded(childReason.id)"
        :is-selected="isChildSelected(childReason.id)"
        :expanded-reasons="expandedReasons"
        :selected-reasons="selectedReasons"
        @toggle-expand="emit('toggleExpand', $event)"
        @toggle-selection="emit('toggleSelection', $event)"
      />
    </div>
  </div>
</template>
